import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ExternalLink, Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface LocationAutocompleteProps {
  value: string;
  lat?: number | null;
  lng?: number | null;
  formattedAddress?: string | null;
  onChange: (location: { address: string; lat: number | null; lng: number | null; formattedAddress?: string | null }) => void;
}

function generateSessionToken(): string {
  // Standard UUID v4 — used by Google Places to bundle Autocomplete+Details into one billed session.
  return crypto.randomUUID();
}

export function LocationAutocomplete({ value, lat, lng, formattedAddress, onChange }: LocationAutocompleteProps) {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const sessionTokenRef = useRef<string>(generateSessionToken());
  const debounceRef = useRef<number | null>(null);
  const reqIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchPredictions = useCallback(async (input: string) => {
    if (!input || input.trim().length < 2) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("places-autocomplete", {
        body: { input, sessionToken: sessionTokenRef.current },
      });
      if (myReqId !== reqIdRef.current) return; // stale response
      if (error || !data?.predictions) {
        setPredictions([]);
        setOpen(false);
        return;
      }
      setPredictions(data.predictions);
      setOpen(data.predictions.length > 0);
      setActiveIndex(-1);
    } catch {
      if (myReqId === reqIdRef.current) {
        setPredictions([]);
        setOpen(false);
      }
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange({ address: next, lat: null, lng: null, formattedAddress: null });

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchPredictions(next);
    }, 300);
  };

  const handleSelect = useCallback(async (p: Prediction) => {
    setOpen(false);
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("places-details", {
        body: { placeId: p.place_id, sessionToken: sessionTokenRef.current },
      });
      if (error || !data?.result) {
        // Fallback: keep the description text as the address with no coords
        onChange({ address: p.description, lat: null, lng: null, formattedAddress: null });
        return;
      }
      const { name, formattedAddress: fa, lat: pLat, lng: pLng } = data.result;
      const displayName = name || p.structured_formatting.main_text || p.description;
      onChange({
        address: displayName,
        lat: pLat,
        lng: pLng,
        formattedAddress: fa && fa !== displayName ? fa : null,
      });
    } catch {
      onChange({ address: p.description, lat: null, lng: null, formattedAddress: null });
    } finally {
      setLoading(false);
      // Start a new session for the next search (Google billing convention)
      sessionTokenRef.current = generateSessionToken();
      setPredictions([]);
    }
  }, [onChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || predictions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(predictions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(0, i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(predictions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="space-y-2">
      <div className="relative">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="relative w-full">
            <Input
              value={value}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (predictions.length > 0) setOpen(true); }}
              placeholder="Search for a location…"
              className="w-full pe-8"
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
            />
            {loading && (
              <Loader2 className="absolute end-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>

        {open && predictions.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-50 start-6 end-0 mt-1 max-h-64 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
          >
            {predictions.map((p, idx) => (
              <li
                key={p.place_id}
                role="option"
                aria-selected={idx === activeIndex}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(p); }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`cursor-pointer px-3 py-2 text-sm border-b last:border-b-0 ${
                  idx === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
                }`}
              >
                <div className="font-medium truncate">{p.structured_formatting.main_text}</div>
                {p.structured_formatting.secondary_text && (
                  <div className="text-xs text-muted-foreground truncate">
                    {p.structured_formatting.secondary_text}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {(formattedAddress || (lat != null && lng != null)) && (
        <div className="ms-6 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">📍 {formattedAddress || `${lat?.toFixed(5)}, ${lng?.toFixed(5)}`}</span>
          {lat != null && lng != null && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 underline hover:text-foreground shrink-0"
            >
              View on map <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
